import React, { useEffect, useState } from "react";
import { Input } from "@/ui-components/ui/input";
import { Textarea } from "@/ui-components/ui/textarea";
import { Coins, Image as ImageIcon, MapPin, Building2, Link as LinkIcon, StickyNote, User } from "lucide-react";
import { toast } from "@/lib/toast";
import {
  useSponsorsApplyImageEdit,
  useSponsorsCreate,
  useSponsorsCreationEntitiesQuery,
  useSponsorsPreviewBackgroundRemoval,
  useSponsorsUploadImage,
} from "@/lib/api/requests/sponsors.requests";
import { CreateSponsorRequestDto } from "@/lib/api/interfaces/sponsors.interface";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { CreateBaseDialog, DialogFormSection } from "@/util-components/dialog/base-dialogs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { ManagedImageField, persistManagedImageDraft } from "@/util-components/media";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

interface CreateSponsorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (sponsorId: string) => void;
}

const createSponsorSchema = z.object({
  name: z.string().min(1, { message: "Name ist erforderlich" }),
  website: z.string().optional(),
  address: z.string().optional(),
  street: z.string().optional(),
  streetNumber: z.string().optional(),
  zip: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  notes: z.string().optional(),
  assignedUserId: z.string().optional(),
  logo: z.instanceof(File).optional(),
});

type CreateSponsorFormValues = z.infer<typeof createSponsorSchema>;

/**
 * Creates sponsors and persists the optional managed logo draft after the
 * sponsor itself exists.
 */
export const CreateSponsorDialog: React.FC<CreateSponsorDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <CreateBaseDialog
      open={open}
      onClose={(open) => onOpenChange(open)}
      icon={Coins}
      title="Neuen Sponsor anlegen"
      description="Erstelle einen neuen Sponsor mit den wichtigsten Stammdaten."
      isSaving={isSaving}
      canSave={canSave}
      formId="create-sponsor-form"
      submitLabel="Sponsor anlegen"
      submitLoadingLabel="Anlegen..."
      maxWidth="lg"
    >
      <CreateSponsorForm
        open={open}
        onOpenChange={onOpenChange}
        onSuccess={onSuccess}
        setIsSaving={setIsSaving}
        setCanSave={setCanSave}
      />
    </CreateBaseDialog>
  );
};

const CreateSponsorForm = ({
  open,
  onOpenChange,
  onSuccess,
  setIsSaving,
  setCanSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (sponsorId: string) => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const NONE_USER_VALUE = "NONE";

  const createSponsorMutation = useSponsorsCreate();
  const { mutateAsync: uploadLogo, isPending: isUploadingLogo } = useSponsorsUploadImage();
  const { mutateAsync: applyImageEdit, isPending: isApplyingImageEdit } = useSponsorsApplyImageEdit();
  const { mutateAsync: previewBackgroundRemoval } = useSponsorsPreviewBackgroundRemoval();

  const creationEntitiesQuery = useSponsorsCreationEntitiesQuery({ enabled: open });
  const users = creationEntitiesQuery.data?.users ?? [];

  const form = useForm<CreateSponsorFormValues>({
    resolver: zodResolver(createSponsorSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      website: "",
      address: "",
      street: "",
      streetNumber: "",
      zip: "",
      city: "",
      country: "",
      notes: "",
      assignedUserId: NONE_USER_VALUE,
      logo: undefined,
    },
  });

  const logoFile = form.watch("logo");
  const isLoading = createSponsorMutation.isPending || isUploadingLogo || isApplyingImageEdit;

  useEffect(() => {
    setIsSaving(isLoading);
  }, [isLoading, setIsSaving]);

  useEffect(() => {
    setCanSave(form.formState.isValid);
  }, [form.formState.isValid, setCanSave]);

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const onSubmit = async (values: CreateSponsorFormValues) => {
    const payload: CreateSponsorRequestDto = {
      name: values.name.trim(),
      address: values.address?.trim() || undefined,
      website: values.website?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      street: values.street?.trim() || undefined,
      streetNumber: values.streetNumber?.trim() || undefined,
      zip: values.zip?.trim() || undefined,
      city: values.city?.trim() || undefined,
      country: values.country?.trim() || undefined,
      assignedUserId: !values.assignedUserId || values.assignedUserId === NONE_USER_VALUE ? undefined : values.assignedUserId,
    } as CreateSponsorRequestDto;

    try {
      const created = await createSponsorMutation.mutateAsync(payload);

      if (values.logo) {
        await persistManagedImageDraft({
          entityId: created._id,
          file: values.logo,
          uploadImage: uploadLogo,
          applyImageEdit,
        });
      }

      toast.success(values.logo ? "Sponsor und Logo erfolgreich angelegt" : "Sponsor erfolgreich angelegt", {
        description: created.name,
        duration: 3000,
      });
      onOpenChange(false);
      onSuccess(created._id);
    } catch (error) {
      console.error("Failed to create sponsor", error);
      toast.error("Fehler beim Anlegen", {
        description: "Der Sponsor konnte nicht angelegt werden. Bitte versuche es erneut.",
        duration: 5000,
        error,
      });
    }
  };

  return (
    <Form {...form}>
      <form id="create-sponsor-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-1 min-h-0 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto p-4 sm:p-6">
          <DialogFormSection title="Sponsorenlogo" icon={ImageIcon}>
            <ManagedImageField
              file={logoFile}
              onFileChange={(nextFile) => form.setValue("logo", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
              disabled={isLoading}
              busyAction={isLoading ? "saving" : null}
              emptyTitle="Logo auswählen"
              previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
            />
          </DialogFormSection>

          <DialogFormSection title="Stammdaten" icon={Building2}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel isOptional={false}>
                      <Building2 className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Name des Sponsors
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="z.B. Muster GmbH"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assignedUserId"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Zuständige Person (optional)
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isLoading || creationEntitiesQuery.isLoading || users.length === 0}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 bg-white border-gray-200 text-left font-normal">
                          <SelectValue placeholder={creationEntitiesQuery.isLoading ? "Lade Benutzer..." : "Niemandem zugewiesen"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NONE_USER_VALUE}>Niemandem zugewiesen</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user._id} value={user._id}>
                            {user.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      <LinkIcon className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Website (optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://www.beispiel.de"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Adresse" icon={MapPin}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormControl>
                      <Input
                        placeholder="z.B. Alleestraße 12, 12345 Musterstadt"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="sm:col-span-2 grid gap-4 sm:grid-cols-[2fr,1fr]">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Straße"
                          {...field}
                          disabled={isLoading}
                          className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="streetNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Nr."
                          {...field}
                          disabled={isLoading}
                          className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="sm:col-span-2 grid gap-4 sm:grid-cols-[1fr,2fr]">
                <FormField
                  control={form.control}
                  name="zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="PLZ"
                          {...field}
                          disabled={isLoading}
                          className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Ort"
                          {...field}
                          disabled={isLoading}
                          className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormControl>
                      <Input
                        placeholder="Land"
                        {...field}
                        disabled={isLoading}
                        className="h-11 bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Notizen" icon={StickyNote}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormControl>
                      <Textarea
                        placeholder="Interessen, bisherige Kontakte, besondere Vereinbarungen..."
                        {...field}
                        disabled={isLoading}
                        className="min-h-[80px] bg-white border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>
        </div>
      </form>
    </Form>
  );
};

